import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Breadcrumbs from "../common/Breadcrumbs";
import Aos from "aos";
import { all_routes } from "../router/all_routes";

const routes = all_routes;

// ─── Service Data ────────────────────────────────────────────────────────────
const serviceDetails: Record<string, {
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  longDescription: string;
  features: string[];
  cta: string;
}> = {
  "vip-concierge": {
    icon: "✧",
    title: "VIP Concierge Services",
    subtitle: "Private Airport Lounge Access",
    description:
      "Experience true exclusivity from the moment you arrive. We provide access to private airport lounges at Geneva and Zurich airports — a serene, premium environment before your flight.",
    longDescription:
      "Our VIP Concierge service is designed for discerning travellers who expect nothing less than perfection. From the moment your flight lands, our dedicated concierge team ensures a seamless, stress-free experience — expedited through immigration, escorted to an exclusive private lounge, and transferred discreetly to your waiting vehicle. Every touchpoint is curated to embody quiet luxury.",
    features: [
      "Private lounge access at Geneva & Zurich airports",
      "Dedicated concierge assistance on arrival & departure",
      "Fast-track security & immigration services",
      "Refreshments & business amenities in the lounge",
      "Seamless coordination with your Eleego chauffeur",
      "Available 24 / 7, 365 days a year",
    ],
    cta: "Enquire About Concierge",
  },
  "hotel-bookings": {
    icon: "◈",
    title: "Luxury Hotel Bookings",
    subtitle: "Exclusive 5-Star Reservations",
    description:
      "We handle exclusive reservations at the finest 5-star hotels in Switzerland and Europe upon request — ensuring you arrive to an experience that matches the journey.",
    longDescription:
      "Eleego's hospitality partnerships span the most coveted addresses in Switzerland and across Europe. Whether you require a suite at Geneva's Beau-Rivage, a chalet in Verbier, or a palace hotel in Paris, our team secures preferential rates, arranges bespoke amenities, and coordinates every detail so your stay is flawless from the first moment to the last.",
    features: [
      "Curated selection of 5-star & palace hotels",
      "Suite & villa reservations on request",
      "Bespoke amenity & welcome gift arrangements",
      "Breakfast, spa & dining reservations included",
      "Corporate & long-stay negotiated rates",
      "VIP early check-in / late check-out coordination",
    ],
    cta: "Request a Hotel Booking",
  },
  "airline-ticketing": {
    icon: "✈",
    title: "Airline Ticketing Services",
    subtitle: "Reservations, Bookings & Itinerary Management",
    description:
      "We take care of all your airline needs — from reservations and ticket bookings to full itinerary management — so you can focus on what matters most.",
    longDescription:
      "From a single business-class seat to coordinating an entire delegation's travel, Eleego's ticketing service handles every aspect of your air travel with precision. We monitor schedules, manage changes in real time, and build connected itineraries that align seamlessly with your ground transfers — giving you one reliable point of contact for every stage of your journey.",
    features: [
      "Flight reservations across all major airlines",
      "Business & first class ticket bookings",
      "Complete multi-leg itinerary planning",
      "Last-minute & urgent booking handling",
      "Group & delegation travel coordination",
      "Real-time flight change & rebooking management",
    ],
    cta: "Enquire About Ticketing",
  },
  "delegation-handling": {
    icon: "◆",
    title: "Official Delegation Handling",
    subtitle: "International Organisations & Diplomatic Missions",
    description:
      "Eleego is experienced in servicing high-profile clients, including international organisations and diplomatic missions based in Geneva, with the utmost discretion and professionalism.",
    longDescription:
      "Geneva is home to hundreds of international organisations and diplomatic missions, and Eleego has become a trusted mobility partner for many of them. We understand the nuances of protocol, security requirements, and the scheduling complexity that accompanies high-profile delegations. Our team operates with complete confidentiality and adapts rapidly to changing requirements.",
    features: [
      "Protocol-compliant transport & logistics",
      "Diplomatic mission & IO support in Geneva",
      "Secure, multi-vehicle fleet for large delegations",
      "Strict confidentiality & NDA compliance",
      "Multilingual, trained chauffeurs",
      "Priority on-demand availability",
    ],
    cta: "Enquire for Delegations",
  },
  "special-occasions": {
    icon: "♛",
    title: "Special Occasion Transportation",
    subtitle: "Weddings, Galas & Exclusive Events",
    description:
      "Make your most special moments unforgettable with our elegant occasion transportation. From weddings to black-tie galas, we ensure every arrival and departure is a statement.",
    longDescription:
      "Every milestone deserves a grand entrance. Eleego's special occasions service pairs immaculate vehicles with discreet, elegantly attired chauffeurs to create a seamless, memorable experience for you and your guests. We coordinate multi-stop logistics, liaise with venue managers, and customise every detail — from floral decorations to chilled champagne on board — to reflect your vision.",
    features: [
      "Decorated vehicles tailored to your theme",
      "Fleet coordination for weddings & galas",
      "Red-carpet arrival & door-to-door service",
      "Champagne, flowers & personalised touches on board",
      "Multi-stop event day itinerary managed by us",
      "Professional & discreet chauffeurs throughout",
    ],
    cta: "Enquire for Your Occasion",
  },
  "cargo-logistics": {
    icon: "◉",
    title: "Cargo & Logistics Services",
    subtitle: "Secure Transfer of Personal Belongings",
    description:
      "Beyond passengers, we provide secure and discreet transfer of personal belongings, valuables, and cargo — handled with the same white-glove care as our passenger services.",
    longDescription:
      "Whether you need sensitive documents delivered across Geneva, luxury goods transported to a hotel, or personal effects moved between residences, Eleego's cargo service offers the same level of care and professionalism as our chauffeur service. Every item is handled with discretion, tracked in real time, and delivered on schedule.",
    features: [
      "Secure transport of personal belongings & valuables",
      "Document & high-value item delivery",
      "Time-sensitive & urgent logistics coordination",
      "Insured transport for high-value goods",
      "Airport-to-destination cargo transfers",
      "Corporate & private logistics solutions",
    ],
    cta: "Enquire About Cargo",
  },
  "winter-transfers": {
    icon: "❄",
    title: "Winter Destination Transfers",
    subtitle: "Luxury Transfers to the Alps & Beyond",
    description:
      "Eleego specialises in luxury transportation to the most exclusive winter destinations in Switzerland and Europe. Arrive in style — whatever the conditions on the road.",
    longDescription:
      "Travelling to high-altitude ski resorts requires specialised expertise. Our winter transfer service provides peace of mind with snow-equipped luxury AWD vehicles, experienced mountaineering chauffeurs, and ample space for all your ski equipment. Whether heading to Zermatt, St. Moritz, Courchevel, or Gstaad, your journey to the slopes will be as comfortable and secure as your stay.",
    features: [
      "Transfers to Zermatt, St. Moritz, Verbier & Gstaad",
      "Courchevel & Val d'Isère also covered",
      "Snow-equipped luxury AWD vehicles",
      "Private chauffeur for ski trips & winter excursions",
      "Luggage & ski equipment assistance",
      "Flexible departure times to beat the traffic",
    ],
    cta: "Book Winter Transfer",
  },
  "davos-wef": {
    icon: "◆",
    title: "Davos WEF Special Services",
    subtitle: "Dedicated Transport for High-Profile Events",
    description:
      "Eleego provides dedicated chauffeur-driven transport for attendees of the Davos World Economic Forum (WEF) and other high-profile business events, ensuring seamless, discreet logistics.",
    longDescription:
      "The World Economic Forum demands intense logistical precision. Our Davos Special Service is tailored to the needs of executives, politicians, and corporate delegates navigating the complexities of WEF. We manage entire fleet deployments, secure compound access, VIP airport transfers to the mountains, and 24-hour on-call availability, ensuring you always arrive at your engagements on time, undisturbed, and in supreme comfort.",
    features: [
      "VIP transfers from Zurich & Geneva airports to Davos",
      "Luxury transport for corporate delegations",
      "Hourly & daily chauffeur service for event attendees",
      "Strict confidentiality & security protocols",
      "Multi-vehicle fleet coordination",
      "Priority 24-hour on-demand availability",
    ],
    cta: "Enquire About Davos WEF",
  },
  "tour-travel": {
    icon: "✈",
    title: "Tour & Travel Services",
    subtitle: "Tailor-Made Private Tours Across Europe",
    description:
      "Eleego offers exclusive private tours to some of the most iconic destinations in Switzerland and Europe. Whether a cultural journey, nature excursion, or city exploration, we craft bespoke itineraries for a truly personal luxury experience.",
    longDescription:
      "Discover the beauty of Europe at your own pace from the back seat of a luxury vehicle. Our private tours are completely bespoke — from scenic drives along Lake Geneva and the Swiss Riviera to multi-day excursions across international borders. With a highly knowledgeable, multilingual chauffeur-guide at the helm, you have the freedom to explore, pause, and immerse yourself in the journey without the rigidity of group travel.",
    features: [
      "Private guided tours across Switzerland & Europe",
      "Cultural, nature & city exploration packages",
      "Tailor-made itineraries to your precise schedule",
      "Multilingual chauffeur-guides available",
      "Luxury vehicles accommodating all group sizes",
      "Flexible departure, route, and return arrangements",
    ],
    cta: "Plan Your Private Tour",
  },
};

// ─── Component ────────────────────────────────────────────────────────────────
const ServiceDetail = () => {
  const location = useLocation();
  const serviceId = new URLSearchParams(location.search).get("service") ?? "";
  const service = serviceDetails[serviceId];

  useEffect(() => {
    Aos.init({ duration: 1200, once: true });
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ height: "70vh", background: "#0A0A0A" }}
      >
        <h2 style={{ color: "var(--luxury-gold)" }}>Service not found.</h2>
        <Link to={routes.services} className="btn lux-btn-gold mt-4">
          Back to Services
        </Link>
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs title={service.title} subtitle={service.title} maintitle="" />

      {/* ── Hero ── */}
      <section
        className="service-detail-hero"
        style={{
          background: "linear-gradient(135deg, #0A0A0A 0%, #111 100%)",
          padding: "80px 0 60px",
          borderBottom: "1px solid rgba(200,164,93,0.15)",
        }}
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center" data-aos="fade-up">
              <div
                style={{
                  fontSize: "56px",
                  marginBottom: "24px",
                  color: "var(--luxury-gold)",
                  filter: "drop-shadow(0 0 20px rgba(200,164,93,0.4))",
                }}
              >
                {service.icon}
              </div>
              <p className="lux-eyebrow mb-3">{service.subtitle}</p>
              <h1 className="lux-split__title" style={{ fontSize: "clamp(28px, 5vw, 48px)" }}>
                {service.title}
              </h1>
              <div className="lux-split__divider mx-auto mt-4 mb-5" />
              <p
                style={{
                  color: "var(--luxury-text-muted)",
                  fontSize: "18px",
                  lineHeight: "1.8",
                  maxWidth: "640px",
                  margin: "0 auto",
                }}
              >
                {service.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Detail Body ── */}
      <section style={{ background: "#0A0A0A", padding: "80px 0" }}>
        <div className="container">
          <div className="row g-5 align-items-start">

            {/* Left — Long Description */}
            <div className="col-lg-6" data-aos="fade-right">
              <p className="lux-eyebrow mb-3">About This Service</p>
              <p
                style={{
                  color: "var(--luxury-text-muted)",
                  fontSize: "16px",
                  lineHeight: "2",
                }}
              >
                {service.longDescription}
              </p>

              <div className="mt-5">
                <Link
                  to={routes.contact}
                  className="btn lux-btn-gold"
                  style={{ padding: "14px 36px", fontSize: "14px", letterSpacing: "1.5px" }}
                >
                  {service.cta} →
                </Link>
                <Link
                  to={routes.services}
                  className="btn ms-3"
                  style={{
                    padding: "14px 28px",
                    fontSize: "14px",
                    letterSpacing: "1px",
                    border: "1px solid rgba(200,164,93,0.3)",
                    color: "var(--luxury-gold)",
                    background: "transparent",
                    borderRadius: "8px",
                  }}
                >
                  ← All Services
                </Link>
              </div>
            </div>

            {/* Right — Feature List */}
            <div className="col-lg-6" data-aos="fade-left">
              <div
                style={{
                  background: "linear-gradient(135deg, #0E0E0E 0%, #141414 100%)",
                  border: "1px solid rgba(200,164,93,0.2)",
                  borderTop: "3px solid var(--luxury-gold)",
                  borderRadius: "12px",
                  padding: "40px",
                }}
              >
                <p className="lux-eyebrow mb-4">What&apos;s Included</p>
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {service.features.map((feat, i) => (
                    <li
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "14px",
                        padding: "14px 0",
                        borderBottom:
                          i < service.features.length - 1
                            ? "1px solid rgba(255,255,255,0.05)"
                            : "none",
                        color: "var(--luxury-text)",
                        fontSize: "15px",
                      }}
                      data-aos="fade-up"
                      data-aos-delay={i * 80}
                    >
                      <span style={{ color: "var(--luxury-gold)", fontSize: "18px", flexShrink: 0 }}>✓</span>
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section
        style={{
          background: "linear-gradient(135deg, #111 0%, #0A0A0A 100%)",
          padding: "60px 0",
          borderTop: "1px solid rgba(200,164,93,0.15)",
        }}
        data-aos="fade-up"
      >
        <div className="container text-center">
          <h3
            style={{
              color: "var(--luxury-text)",
              fontFamily: "'Playfair Display', serif",
              fontSize: "28px",
              marginBottom: "12px",
            }}
          >
            Ready to experience <span style={{ color: "var(--luxury-gold)" }}>{service.title}</span>?
          </h3>
          <p style={{ color: "var(--luxury-text-muted)", marginBottom: "32px" }}>
            Contact our team and we&apos;ll arrange everything for you.
          </p>
          <Link to={routes.contact} className="btn lux-btn-gold" style={{ padding: "16px 48px", fontSize: "15px" }}>
            Get In Touch →
          </Link>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
